/**
 * PSTEditorView Component
 *
 * Simplified PST container view that shows progress of child document
 * redactions and provides a Finalize button to create the final ZIP export.
 *
 * PST files are now treated as "container" documents - their children
 * (individual MSG/EML files) are extracted and processed separately.
 */

import { useMemo } from 'react';
import { Archive, Check, Clock, AlertCircle, Download, FileText } from 'lucide-react';
import { Button } from '../ui/Button';
import { Progress } from '../ui/progress';
import type { CaseDocument, DocumentStatus } from '../../types';

interface PSTEditorViewProps {
  /** The PST document ID */
  documentId: string;
  /** The PST document name */
  documentName: string;
  /** Child documents extracted from the PST */
  childDocuments: CaseDocument[];
  /** Callback when user clicks Finalize */
  onFinalize: () => void;
  /** Loading state for finalize operation */
  isFinalizingPst?: boolean;
}

export function PSTEditorView({
  documentId: _documentId,
  documentName,
  childDocuments,
  onFinalize,
  isFinalizingPst = false,
}: PSTEditorViewProps) {
  // _documentId kept for potential future use
  void _documentId;

  // Calculate status counts
  const stats = useMemo(() => {
    const statusCounts: Record<DocumentStatus, number> = {
      unprocessed: 0,
      needs_redaction: 0,
      redacted: 0,
      completed: 0,
    };

    childDocuments.forEach((doc) => {
      statusCounts[doc.status]++;
    });

    const total = childDocuments.length;
    const completed = statusCounts.completed;
    const inProgress = statusCounts.needs_redaction + statusCounts.redacted;
    const pending = statusCounts.unprocessed;
    const allCompleted = completed === total && total > 0;
    const progressPercent = total > 0 ? Math.round((completed / total) * 100) : 0;

    return {
      ...statusCounts,
      total,
      completed,
      inProgress,
      pending,
      allCompleted,
      progressPercent,
    };
  }, [childDocuments]);

  return (
    <div className="flex flex-col h-full bg-muted">
      {/* Header */}
      <div className="bg-card border-b border-border px-6 py-3">
        <div className="flex items-center gap-3">
          <Archive className="w-5 h-5 text-amber-600" />
          <div>
            <h2 className="font-medium text-sm">PST Container</h2>
            <p className="text-xs text-muted-foreground truncate max-w-md" title={documentName}>
              {documentName}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-md w-full text-center space-y-8">
          {/* Icon and Title */}
          <div className="space-y-4">
            <div className="w-20 h-20 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
              <Archive className="w-10 h-10 text-amber-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground">PST Container Document</h1>
              <p className="text-muted-foreground mt-1">
                {stats.total} email{stats.total !== 1 ? 's' : ''} extracted
              </p>
            </div>
          </div>

          {/* Progress Section */}
          <div className="bg-card rounded-lg border border-border p-6 space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Redaction Progress</span>
              <span className="font-medium">
                {stats.completed} / {stats.total} completed
              </span>
            </div>

            <Progress value={stats.progressPercent} className="h-2" />

            {/* Status Breakdown */}
            <div className="grid grid-cols-3 gap-4 pt-2">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1.5 text-gray-500">
                  <Clock className="w-4 h-4" />
                  <span className="text-lg font-semibold">{stats.pending}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Pending</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1.5 text-amber-500">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-lg font-semibold">{stats.inProgress}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">In Progress</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1.5 text-green-500">
                  <Check className="w-4 h-4" />
                  <span className="text-lg font-semibold">{stats.completed}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Completed</p>
              </div>
            </div>
          </div>

          {/* Finalize Button */}
          <div className="space-y-3">
            <Button
              size="lg"
              onClick={onFinalize}
              disabled={!stats.allCompleted || isFinalizingPst}
              className="w-full"
            >
              {isFinalizingPst ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                  Finalizing...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Finalize PST
                </>
              )}
            </Button>

            {!stats.allCompleted && stats.total > 0 && (
              <p className="text-xs text-muted-foreground">
                Complete all {stats.total - stats.completed} remaining email{stats.total - stats.completed !== 1 ? 's' : ''} to finalize
              </p>
            )}

            {stats.allCompleted && (
              <p className="text-xs text-green-600">
                All emails completed! Click Finalize to create the redacted ZIP archive.
              </p>
            )}
          </div>

          {/* Instructions */}
          <div className="pt-4 border-t border-border">
            <div className="flex items-start gap-3 text-left">
              <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground">
                  Select individual emails from the Documents panel
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Each email can be redacted independently. Expand this PST in the
                  Documents panel to view and select individual messages.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <div className="bg-card border-t border-border px-6 py-2 text-xs text-muted-foreground">
        <div className="flex items-center justify-between">
          <span>PST Container • {stats.total} children</span>
          <span>{stats.progressPercent}% complete</span>
        </div>
      </div>
    </div>
  );
}
